import { devLogger } from "@/utils/dev-logger.util";
/**
 * Accordion component for collapse/expand functionality
 * Used for CollapseItemBlock and similar accordion patterns
 */
export const Accordion = (element) => {
    const toggle = element.querySelector('[data-action="accordion-toggle"]');
    const content = element.querySelector('[data-action="accordion-content"]');
    if (!toggle || !content) {
        devLogger.warn("Accordion: Required elements not found (toggle or content)");
        return;
    }
    const initialExpanded = element.dataset.initialExpanded === 'true';
    /**
     * Get current expanded state from DOM
     */
    const isExpanded = () => toggle.getAttribute('aria-expanded') === 'true';
    /**
     * Lock max-height to current scroll height (needed before animating from "none")
     */
    const lockContentHeight = () => {
        content.style.maxHeight = `${content.scrollHeight}px`;
    };
    /**
     * Handle transition end - allow natural height when fully expanded
     */
    const handleTransitionEnd = (event) => {
        if (event.propertyName !== 'max-height')
            return;
        if (isExpanded()) {
            content.style.maxHeight = 'none';
            content.style.overflow = '';
        }
    };
    /**
     * Update the accordion state
     */
    const updateAccordionState = (expanded) => {
        toggle.setAttribute('aria-expanded', String(expanded));
        content.setAttribute('aria-hidden', String(!expanded));
        content.style.overflow = 'hidden';
        if (expanded) {
            if (content.style.maxHeight === 'none') {
                lockContentHeight();
            }
            content.style.maxHeight = `${content.scrollHeight}px`;
            return;
        }
        if (content.style.maxHeight === 'none') {
            lockContentHeight();
            requestAnimationFrame(() => {
                content.style.maxHeight = '0';
            });
            return;
        }
        content.style.maxHeight = '0';
    };
    /**
     * Toggle accordion state
     */
    const toggleAccordion = (event) => {
        event.preventDefault();
        updateAccordionState(!isExpanded());
    };
    /**
     * Handle keyboard events (Enter or Space)
     */
    const handleKeydown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            toggleAccordion(event);
        }
    };
    toggle.addEventListener('click', toggleAccordion);
    toggle.addEventListener('keydown', handleKeydown);
    content.addEventListener('transitionend', handleTransitionEnd);
    if (initialExpanded) {
        toggle.setAttribute('aria-expanded', 'true');
        content.setAttribute('aria-hidden', 'false');
        content.style.maxHeight = 'none';
        content.style.overflow = '';
    }
    else {
        toggle.setAttribute('aria-expanded', 'false');
        content.setAttribute('aria-hidden', 'true');
        content.style.maxHeight = '0';
        content.style.overflow = 'hidden';
    }
    devLogger.log("Accordion initialized", element);
    return () => {
        toggle.removeEventListener('click', toggleAccordion);
        toggle.removeEventListener('keydown', handleKeydown);
        content.removeEventListener('transitionend', handleTransitionEnd);
    };
};
